import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../Modal';
import { DuplicateClientAlert } from '../DuplicateClientModal';
import { DuplicateClientError, type ExistingClientRef } from '../../api/clients';
import { SortableTh } from '../SortableTh';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import {
  convertIntakeResponse,
  fetchIntakeFormResponses,
  updateIntakeResponseTags,
  type IntakeResponseRow,
} from '../../api/intakeFormResponses';
import {
  LEAD_FIELD_TAG_SUGGESTIONS,
  LEAD_PRIORITY_TAGS,
  type WebsiteFormLeadTag,
} from '../../api/websiteFormLeads';

/** Answer columns shown inline; the rest stay in the detail modal. */
const MAX_ANSWER_COLUMNS = 6;

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function tagsOf(row: IntakeResponseRow): WebsiteFormLeadTag[] {
  return Array.isArray(row.tags) ? row.tags : [];
}

function priorityOf(row: IntakeResponseRow): string {
  return tagsOf(row).find((t) => t.kind === 'priority')?.label ?? '';
}

function fieldTagsOf(row: IntakeResponseRow): string[] {
  return tagsOf(row)
    .filter((t) => t.kind === 'field')
    .map((t) => t.label);
}

/** Answers by label, first one wins when a form repeats a label. */
function answerMap(row: IntakeResponseRow): Map<string, string> {
  const map = new Map<string, string>();
  for (const cell of row.cells ?? []) {
    if (!map.has(cell.label)) map.set(cell.label, cell.value);
  }
  return map;
}

/** First answer whose field type or label looks like what we're after. */
function findAnswer(row: IntakeResponseRow, type: string | null, labelPattern: RegExp): string {
  const cells = row.cells ?? [];
  if (type) {
    const byType = cells.find((c) => c.field_type === type && c.value.trim());
    if (byType) return byType.value.trim();
  }
  const byLabel = cells.find((c) => labelPattern.test(c.label) && c.value.trim());
  return byLabel ? byLabel.value.trim() : '';
}

/** Values the confirmation modal shows and the user may edit before saving. */
interface ConvertDraft {
  company: string;
  point_of_contact: string;
  contact_email: string;
  contact_phone: string;
  source: string;
  industry: string;
  notes: string;
}

function draftFromResponse(row: IntakeResponseRow): ConvertDraft {
  const company = findAnswer(row, null, /company|organi[sz]ation|business|facility|employer/i);
  const contact = findAnswer(row, null, /^(full |first |last )?name$|contact|attendee/i);
  const email = findAnswer(row, 'email', /e-?mail/i);
  const phone = findAnswer(row, 'phone', /phone|mobile|cell/i);
  const industry =
    findAnswer(row, null, /industry|sector|market|vertical/i) || fieldTagsOf(row)[0] || '';
  const answerLines = (row.cells ?? [])
    .filter((c) => c.value.trim())
    .map((c) => `${c.label}: ${c.value.trim()}`);
  const notes = [`Captured at ${row.event_name} on ${formatDateTime(row.created_at)}.`, '', ...answerLines]
    .join('\n')
    .trim();
  return {
    company: company || contact || row.event_name,
    point_of_contact: contact || company,
    contact_email: email,
    contact_phone: phone,
    source: `Event — ${row.event_name}`,
    industry,
    notes,
  };
}

interface Props {
  userId: number;
}

/**
 * Every event intake response in one table, with the website-lead tag
 * vocabulary and the same promote-to-client-database flow.
 */
const IntakeResponsesSection: React.FC<Props> = ({ userId }) => {
  const [rows, setRows] = useState<IntakeResponseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState<'All' | number>('All');
  const [selected, setSelected] = useState<IntakeResponseRow | null>(null);
  const [customTag, setCustomTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [converting, setConverting] = useState<IntakeResponseRow | null>(null);
  const [convertDraft, setConvertDraft] = useState<ConvertDraft | null>(null);
  const [convertEditing, setConvertEditing] = useState(false);
  const [convertSaving, setConvertSaving] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [convertDuplicate, setConvertDuplicate] = useState<ExistingClientRef | null>(null);
  const [convertDone, setConvertDone] = useState<{ name: string; clientId: number } | null>(null);

  const { sortKey, sortDir, toggleSort } = useTableSortState();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setRows(await fetchIntakeFormResponses(userId));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load intake form responses.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const events = useMemo(() => {
    const seen = new Map<number, string>();
    for (const r of rows) if (!seen.has(r.event_id)) seen.set(r.event_id, r.event_name);
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [rows]);

  const replaceRow = useCallback((updated: IntakeResponseRow) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setSelected((prev) => (prev && prev.id === updated.id ? updated : prev));
  }, []);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (eventFilter !== 'All' && r.event_id !== eventFilter) return false;
      if (!term) return true;
      return [
        r.event_name,
        ...(r.cells ?? []).flatMap((c) => [c.label, c.value]),
        ...tagsOf(r).map((t) => t.label),
      ]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [rows, search, eventFilter]);

  /**
   * Forms differ per event, so the answer columns are the labels the visible
   * responses actually use, most common first.
   */
  const answerColumns = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of visible) {
      answerMap(r).forEach((_value, label) => {
        counts.set(label, (counts.get(label) ?? 0) + 1);
      });
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, MAX_ANSWER_COLUMNS)
      .map(([label]) => label);
  }, [visible]);

  const sorted = useMemo(
    () =>
      sortRows(visible, sortKey, sortDir, (r, key) => {
        switch (key) {
          case 'received':
            return r.created_at ?? '';
          case 'event':
            return r.event_name;
          case 'priority':
            return priorityOf(r);
          case 'client':
            return r.converted_company ?? '';
          default:
            return answerMap(r).get(key) ?? '';
        }
      }),
    [visible, sortKey, sortDir]
  );

  const saveTags = async (row: IntakeResponseRow, tags: WebsiteFormLeadTag[]) => {
    setSaving(true);
    setSaveError('');
    try {
      replaceRow(await updateIntakeResponseTags(row.id, userId, tags));
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to update tags.');
    } finally {
      setSaving(false);
    }
  };

  const togglePriority = (row: IntakeResponseRow, label: string) => {
    const others = tagsOf(row).filter((t) => t.kind !== 'priority');
    const next: WebsiteFormLeadTag[] =
      priorityOf(row) === label ? others : [...others, { kind: 'priority', label }];
    void saveTags(row, next);
  };

  const toggleFieldTag = (row: IntakeResponseRow, label: string) => {
    const clean = label.trim();
    if (!clean) return;
    const current = tagsOf(row);
    const has = current.some(
      (t) => t.kind === 'field' && t.label.toLowerCase() === clean.toLowerCase()
    );
    const next = has
      ? current.filter((t) => !(t.kind === 'field' && t.label.toLowerCase() === clean.toLowerCase()))
      : [...current, { kind: 'field' as const, label: clean }];
    void saveTags(row, next);
  };

  const openConvert = (row: IntakeResponseRow) => {
    setConverting(row);
    setConvertDraft(draftFromResponse(row));
    setConvertEditing(false);
    setConvertError('');
    setConvertDuplicate(null);
  };

  const setDraftField = <K extends keyof ConvertDraft>(key: K, value: ConvertDraft[K]) =>
    setConvertDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  const confirmConvert = async () => {
    if (!converting || !convertDraft) return;
    setConvertSaving(true);
    setConvertError('');
    setConvertDuplicate(null);
    try {
      const result = await convertIntakeResponse(converting.id, {
        user_id: userId,
        ...convertDraft,
      });
      replaceRow(result.response);
      setConvertDone({ name: convertDraft.company, clientId: result.client_id });
      setConverting(null);
      setConvertDraft(null);
    } catch (e) {
      if (e instanceof DuplicateClientError) setConvertDuplicate(e.existing);
      else setConvertError(e instanceof Error ? e.message : 'Failed to add lead.');
    } finally {
      setConvertSaving(false);
    }
  };

  const renderTagPills = (row: IntakeResponseRow) => {
    const priority = priorityOf(row);
    const fields = fieldTagsOf(row);
    if (!priority && fields.length === 0) return <span className="website-lead-muted">—</span>;
    return (
      <span className="website-lead-tag-list">
        {priority ? (
          <span className={`website-lead-tag website-lead-tag--priority-${priority.toLowerCase()}`}>
            {priority}
          </span>
        ) : null}
        {fields.map((f) => (
          <span key={f} className="website-lead-tag">
            {f}
          </span>
        ))}
      </span>
    );
  };

  const unconvertedCount = rows.filter((r) => r.converted_client_id == null).length;

  return (
    <section className="events-table-section" aria-labelledby="intake-responses-heading">
      <h2 className="events-section-title" id="intake-responses-heading">
        Intake Form Responses <span className="events-section-count">({rows.length})</span>
        {unconvertedCount > 0 ? (
          <span className="website-lead-new-badge">{unconvertedCount} not yet added</span>
        ) : null}
      </h2>
      <p className="page-subtitle website-lead-intro">
        Everything captured by the intake forms on each event. Tag a response and add it to the
        client database as a lead.
      </p>

      <div className="website-lead-controls">
        <input
          type="search"
          className="website-lead-search"
          placeholder="Search answers, event, tags…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search intake form responses"
        />
        <select
          className="website-lead-status-select"
          value={eventFilter === 'All' ? 'All' : String(eventFilter)}
          onChange={(e) => setEventFilter(e.target.value === 'All' ? 'All' : Number(e.target.value))}
          aria-label="Filter by event"
        >
          <option value="All">All events</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>
              {ev.name}
            </option>
          ))}
        </select>
      </div>

      {loadError ? (
        <p className="create-order-error" role="alert">
          {loadError}
        </p>
      ) : null}
      {convertDone ? (
        <p className="marketing-order-notice" role="status">
          {convertDone.name} was added to the client database and the sales team has been notified.{' '}
          <Link to={`/lead/${convertDone.clientId}`}>Open the lead</Link>.
        </p>
      ) : null}

      {loading ? (
        <p className="page-subtitle">Loading intake form responses…</p>
      ) : (
        <div className="events-table-wrap">
          <table className="tasks-search-results-table website-lead-table">
            <thead>
              <tr>
                <SortableTh label="Received" columnKey="received" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Event" columnKey="event" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                {answerColumns.map((label) => (
                  <SortableTh
                    key={label}
                    label={label}
                    columnKey={label}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                ))}
                <SortableTh label="Tags" columnKey="priority" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Actions" columnKey="client" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={answerColumns.length + 4} className="events-empty-cell">
                    {rows.length === 0
                      ? 'No intake form responses yet. They appear here as soon as an event form is filled out.'
                      : 'No responses match this search.'}
                  </td>
                </tr>
              ) : (
                sorted.map((r) => {
                  const answers = answerMap(r);
                  return (
                    <tr
                      key={r.id}
                      className="events-table-row-clickable"
                      onClick={() => {
                        setSelected(r);
                        setCustomTag('');
                        setSaveError('');
                      }}
                    >
                      <td data-label="Received">{formatDateTime(r.created_at)}</td>
                      <td data-label="Event">
                        <Link
                          to={`/events/${r.event_id}`}
                          className="website-lead-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {r.event_name}
                        </Link>
                      </td>
                      {answerColumns.map((label) => (
                        <td key={label} data-label={label}>
                          {answers.get(label) || <span className="website-lead-muted">—</span>}
                        </td>
                      ))}
                      <td data-label="Tags">{renderTagPills(r)}</td>
                      <td data-label="Actions">
                        <div onClick={(e) => e.stopPropagation()}>
                          {r.converted_client_id != null ? (
                            <Link to={`/lead/${r.converted_client_id}`} className="website-lead-link">
                              {r.converted_company || 'Open the lead'}
                            </Link>
                          ) : (
                            <button
                              type="button"
                              className="website-lead-status-btn"
                              onClick={() => openConvert(r)}
                            >
                              Add to client database
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Full answers plus the tag pickers. */}
      <Modal
        isOpen={selected != null}
        onClose={() => setSelected(null)}
        title={selected ? `Response from ${selected.event_name}` : 'Response'}
        wide
      >
        {selected ? (
          <>
            <div className="modal-body">
              {saveError ? (
                <p className="create-order-error" role="alert">
                  {saveError}
                </p>
              ) : null}

              <dl className="website-lead-details">
                <div>
                  <dt>Event</dt>
                  <dd>
                    <Link to={`/events/${selected.event_id}`}>{selected.event_name}</Link>
                  </dd>
                </div>
                <div>
                  <dt>Form</dt>
                  <dd>{selected.form_title || '—'}</dd>
                </div>
                <div>
                  <dt>Received</dt>
                  <dd>{formatDateTime(selected.created_at)}</dd>
                </div>
              </dl>

              <h4 className="website-lead-subheading">Answers</h4>
              {(selected.cells ?? []).length === 0 ? (
                <p className="website-lead-muted">This response is empty.</p>
              ) : (
                <dl className="website-lead-details">
                  {(selected.cells ?? []).map((c, i) => (
                    <div key={`${c.label}-${i}`}>
                      <dt>{c.label}</dt>
                      <dd>{c.value || '—'}</dd>
                    </div>
                  ))}
                </dl>
              )}

              <h4 className="website-lead-subheading">Priority</h4>
              <div className="website-lead-tag-picker">
                {LEAD_PRIORITY_TAGS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    disabled={saving}
                    className={`website-lead-tag-btn${
                      priorityOf(selected) === p ? ' website-lead-tag-btn--on' : ''
                    }`}
                    onClick={() => togglePriority(selected, p)}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <h4 className="website-lead-subheading">Field</h4>
              <div className="website-lead-tag-picker">
                {Array.from(new Set([...LEAD_FIELD_TAG_SUGGESTIONS, ...fieldTagsOf(selected)])).map(
                  (f) => (
                    <button
                      key={f}
                      type="button"
                      disabled={saving}
                      className={`website-lead-tag-btn${
                        fieldTagsOf(selected).some((t) => t.toLowerCase() === f.toLowerCase())
                          ? ' website-lead-tag-btn--on'
                          : ''
                      }`}
                      onClick={() => toggleFieldTag(selected, f)}
                    >
                      {f}
                    </button>
                  )
                )}
              </div>
              <div className="website-lead-tag-add">
                <input
                  type="text"
                  value={customTag}
                  placeholder="Add another field…"
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      toggleFieldTag(selected, customTag);
                      setCustomTag('');
                    }
                  }}
                />
                <button
                  type="button"
                  disabled={saving || !customTag.trim()}
                  className="website-lead-status-btn"
                  onClick={() => {
                    toggleFieldTag(selected, customTag);
                    setCustomTag('');
                  }}
                >
                  Add tag
                </button>
              </div>

              <h4 className="website-lead-subheading">Client database</h4>
              {selected.converted_client_id != null ? (
                <p className="website-lead-muted">
                  Added as a lead{selected.converted_by_username ? ` by ${selected.converted_by_username}` : ''}.{' '}
                  <Link to={`/lead/${selected.converted_client_id}`}>Open the lead</Link>.
                </p>
              ) : (
                <button
                  type="button"
                  className="update-button"
                  onClick={() => {
                    const row = selected;
                    setSelected(null);
                    openConvert(row);
                  }}
                >
                  Add to client database
                </button>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="cancel-button" onClick={() => setSelected(null)}>
                Close
              </button>
            </div>
          </>
        ) : null}
      </Modal>

      {/* Confirmation before the response becomes a lead in the client database. */}
      <Modal
        isOpen={converting != null}
        onClose={() => (convertSaving ? undefined : setConverting(null))}
        title="Add to client database"
        wide
      >
        {converting && convertDraft ? (
          <>
            <div className="modal-body">
              <p className="page-subtitle">
                {convertEditing
                  ? 'Adjust anything that needs cleaning up, then confirm.'
                  : 'Check the details below before adding this lead. Choose Edit to change anything.'}
              </p>
              {convertError ? (
                <p className="create-order-error" role="alert">
                  {convertError}
                </p>
              ) : null}
              {convertDuplicate ? <DuplicateClientAlert existing={convertDuplicate} /> : null}

              <div className="marketing-order-grid">
                <label className="marketing-order-field">
                  <span>Company *</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.company}
                      onChange={(e) => setDraftField('company', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.company || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Point of contact *</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.point_of_contact}
                      onChange={(e) => setDraftField('point_of_contact', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.point_of_contact || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Email</span>
                  {convertEditing ? (
                    <input
                      type="email"
                      value={convertDraft.contact_email}
                      onChange={(e) => setDraftField('contact_email', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.contact_email || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Phone</span>
                  {convertEditing ? (
                    <input
                      type="tel"
                      value={convertDraft.contact_phone}
                      onChange={(e) => setDraftField('contact_phone', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.contact_phone || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Source</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.source}
                      onChange={(e) => setDraftField('source', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.source || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Industry</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.industry}
                      onChange={(e) => setDraftField('industry', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.industry || '—'}</p>
                  )}
                </label>
              </div>
              <label className="marketing-order-field">
                <span>Notes</span>
                {convertEditing ? (
                  <textarea
                    rows={6}
                    value={convertDraft.notes}
                    onChange={(e) => setDraftField('notes', e.target.value)}
                  />
                ) : (
                  <p className="website-lead-message">{convertDraft.notes || '—'}</p>
                )}
              </label>
              <p className="website-lead-muted">Confirming emails the sales team.</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={() => setConverting(null)}
                disabled={convertSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="website-lead-status-btn"
                onClick={() => setConvertEditing((v) => !v)}
                disabled={convertSaving}
              >
                {convertEditing ? 'Done editing' : 'Edit'}
              </button>
              <button
                type="button"
                className="update-button"
                onClick={() => void confirmConvert()}
                disabled={
                  convertSaving ||
                  !convertDraft.company.trim() ||
                  !convertDraft.point_of_contact.trim()
                }
              >
                {convertSaving ? 'Adding…' : 'Confirm & add lead'}
              </button>
            </div>
          </>
        ) : null}
      </Modal>
    </section>
  );
};

export default IntakeResponsesSection;
